import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Users, Home, Scissors, Zap, Volume2, Heart, DollarSign, Scale, GraduationCap, HeartPulse, Bone, Activity, BookOpen, Clock } from "lucide-react";
import { useEntitlement } from "@/lib/entitlement";
import { usePageMeta } from "@/lib/usePageMeta";
import { getBreed, getCost, getTrainingTips, getHealthNutrition, getGuides } from "@/lib/api";
import { lbsRangeToKg, inRangeToCm } from "@/lib/units";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const StatBar = ({ label, value, icon: Icon }) => (
  <div>
    <div className="flex items-center gap-2 text-sm text-accent/80 mb-1.5">
      <Icon size={14} strokeWidth={2.2} />
      <span>{label}</span>
      <span className="ml-auto font-semibold">{value}/5</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div
        className="h-full bg-primary rounded-full transition-all duration-500"
        style={{ width: `${(value / 5) * 100}%` }}
      />
    </div>
  </div>
);

// Pick the training/behaviour guides most relevant to a breed's traits.
// Always leads with the foundational learning-theory guide, then adds
// trait-driven picks. Returns ordered slugs (deduped).
function recommendedGuideSlugs(breed) {
  const slugs = ["how-dogs-learn"];
  if (breed.energy >= 4) { slugs.push("reliable-recall", "loose-lead-walking"); }
  if (breed.hours_alone_ok <= 2) slugs.push("separation-anxiety");
  if (breed.barking >= 4 || breed.novice_ok <= 2) slugs.push("lead-reactivity");
  if (breed.novice_ok <= 2 || breed.good_with_pets <= 2) slugs.push("resource-guarding");
  return [...new Set(slugs)];
}

export default function BreedProfile() {
  const { id } = useParams();
  const { unlocked } = useEntitlement();
  const [breed, setBreed] = useState(null);
  const [cost, setCost] = useState(null);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [trainingGuides, setTrainingGuides] = useState([]);
  const [tier, setTier] = useState("medium");
  const [insurance, setInsurance] = useState(true);
  const [compareIds, setCompareIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fetchright.compareIds") || "[]"); }
    catch { return []; }
  });
  const persistCompare = (next) => {
    setCompareIds(next);
    localStorage.setItem("fetchright.compareIds", JSON.stringify(next));
  };
  const inCompare = compareIds.includes(id);

  usePageMeta(
    breed && `${breed.name} — Temperament, Cost & Suitability | Fetchright`,
    breed &&
      `${breed.name}: ${(breed.summary || "").slice(0, 90)} Typical puppy price ` +
      `$${breed.purchase_price?.min_aud?.toLocaleString()}–$${breed.purchase_price?.max_aud?.toLocaleString()} AUD, ` +
      `plus monthly costs, family fit and apartment suitability for Australian owners.`
  );

  const toggleCompare = () => {
    if (inCompare) persistCompare(compareIds.filter((x) => x !== id));
    else persistCompare([...compareIds.filter((x) => x !== id), id].slice(-3));
  };

  useEffect(() => {
    getBreed(id).then(setBreed);
  }, [id]);

  useEffect(() => {
    setTips(null);
    setTipsLoading(true);
    getTrainingTips(id)
      .then((d) => setTips(d.tips || []))
      .catch(() => setTips([]))
      .finally(() => setTipsLoading(false));
  }, [id]);

  useEffect(() => {
    setHealth(null);
    setHealthLoading(true);
    getHealthNutrition(id)
      .then((d) => setHealth(d.sections || []))
      .catch(() => setHealth([]))
      .finally(() => setHealthLoading(false));
  }, [id]);

  useEffect(() => {
    getGuides()
      .then((all) => setTrainingGuides(all.filter((g) => g.category === "Training & Behaviour")))
      .catch(() => setTrainingGuides([]));
  }, []);

  useEffect(() => {
    if (breed) getCost({ breed_id: breed.id, location_tier: tier, insurance }).then(setCost);
  }, [breed, tier, insurance]);

  if (!breed) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const recSlugs = recommendedGuideSlugs(breed);
  const recGuides = recSlugs
    .map((s) => trainingGuides.find((g) => g.slug === s))
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      {/* Hero */}
      <div className="grid md:grid-cols-2 gap-10 items-stretch">
        <div className="rounded-[2rem] overflow-hidden aspect-[4/3] warm-shadow">
          <img src={breed.image} alt={breed.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center">
          <span className="text-primary text-xs uppercase tracking-wider font-semibold">
            {breed.group} · {breed.size}
          </span>
          <h1 data-testid="breed-name" className="font-display font-bold text-5xl text-accent mt-3">
            {breed.name}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{breed.summary}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {breed.temperament.map((t) => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-muted text-accent text-xs font-medium">
                {t}
              </span>
            ))}
          </div>
          {unlocked && (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="breed-compare-toggle"
                onClick={toggleCompare}
                className={`pill-btn inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                  inCompare
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                <Scale size={14} />
                {inCompare ? "In your comparison" : "Add to compare"}
              </button>
              {compareIds.length >= 2 && (
                <Link
                  to={`/compare?ids=${compareIds.join(",")}`}
                  data-testid="breed-compare-open"
                  className="pill-btn inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold"
                >
                  Compare {compareIds.length} →
                </Link>
              )}
            </div>
          )}
          <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-muted-foreground">Weight</div><div className="font-display font-bold text-lg text-accent">{lbsRangeToKg(breed.weight_lbs)} kg</div><div className="text-xs text-muted-foreground">{breed.weight_lbs} lbs</div></div>
            <div><div className="text-muted-foreground">Height</div><div className="font-display font-bold text-lg text-accent">{inRangeToCm(breed.height_in)} cm</div><div className="text-xs text-muted-foreground">{breed.height_in}&quot;</div></div>
            <div><div className="text-muted-foreground">Lifespan</div><div className="font-display font-bold text-lg text-accent">{breed.lifespan_years}y</div></div>
          </div>
        </div>
      </div>

      {/* Traits */}
      <div className="mt-16 grid md:grid-cols-2 gap-8">
        <div className="rounded-3xl bg-card border border-border/60 p-8 warm-shadow">
          <h2 className="font-display font-bold text-2xl text-accent">Traits at a glance</h2>
          <div className="mt-6 space-y-5">
            <StatBar label="Energy" value={breed.energy} icon={Zap} />
            <StatBar label="Trainability" value={breed.trainability} icon={Heart} />
            <StatBar label="Grooming needs" value={breed.grooming} icon={Scissors} />
            <StatBar label="Barking" value={breed.barking} icon={Volume2} />
            <StatBar label="Good with kids" value={breed.good_with_kids} icon={Users} />
            <StatBar label="Apartment ok" value={breed.apartment_ok} icon={Home} />
          </div>
        </div>

        {/* Cost calculator */}
        <div className="rounded-3xl bg-accent text-accent-foreground p-8 warm-shadow">
          <div className="flex items-center gap-2">
            <DollarSign size={18} />
            <h2 className="font-display font-bold text-2xl">Cost calculator</h2>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-accent-foreground/70">Location tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger data-testid="cost-tier-select" className="mt-1.5 rounded-full bg-accent-foreground text-accent border-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Regional / rural</SelectItem>
                  <SelectItem value="medium">Brisbane, Adelaide, Perth</SelectItem>
                  <SelectItem value="high">Sydney, Melbourne metro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <Switch
                id="ins"
                data-testid="cost-insurance-switch"
                checked={insurance}
                onCheckedChange={setInsurance}
              />
              <Label htmlFor="ins" className="text-sm">Include insurance</Label>
            </div>
          </div>

          {cost && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-wider text-accent-foreground/70">Estimated monthly</div>
              <div data-testid="cost-monthly" className="font-display font-bold text-5xl md:text-6xl mt-1">
                ${cost.monthly_total}
                <span className="text-lg text-accent-foreground/60 font-sans font-normal"> AUD/mo</span>
              </div>
              <div className="mt-2 text-sm text-accent-foreground/70">
                ≈ ${cost.annual_total.toLocaleString()} AUD/year · First year with setup ≈ ${cost.first_year_total.toLocaleString()} AUD
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                {Object.entries(cost.monthly_breakdown).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-accent-foreground/10 pb-1.5">
                    <span className="capitalize text-accent-foreground/80">{k}</span>
                    <span className="font-semibold">${v}</span>
                  </div>
                ))}
              </div>

              {breed.purchase_price && (
                <div data-testid="purchase-price-block" className="mt-8 rounded-2xl bg-accent-foreground/10 p-5">
                  <div className="text-xs uppercase tracking-wider text-accent-foreground/70">
                    Upfront — buying the pup
                  </div>
                  <div data-testid="purchase-price-range" className="font-display font-bold text-3xl mt-1">
                    ${breed.purchase_price.min_aud.toLocaleString()}–${breed.purchase_price.max_aud.toLocaleString()}
                    <span className="text-sm text-accent-foreground/60 font-sans font-normal"> AUD one-off</span>
                  </div>
                  <div className="mt-2 text-xs text-accent-foreground/70 leading-relaxed">
                    Typical pet-quality range from registered Australian breeders.
                    Adopting instead? Rescues charge ≈ ${breed.purchase_price.adoption_min_aud}–${breed.purchase_price.adoption_max_aud} adoption fee.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Training tips */}
      <div className="mt-16" data-testid="training-tips-section">
        <div className="flex items-center gap-3 mb-2">
          <GraduationCap size={22} className="text-primary" />
          <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">
            Training tips for a {breed.name}
          </h2>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          Practical, breed-specific advice tuned to a {breed.name}&apos;s temperament, energy and trainability.
        </p>

        {tipsLoading ? (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="training-tips-loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-3xl border border-border/60 bg-card p-6 animate-pulse">
                <div className="h-4 w-1/3 bg-muted rounded mb-3" />
                <div className="h-3 w-full bg-muted rounded mb-2" />
                <div className="h-3 w-4/5 bg-muted rounded" />
              </div>
            ))}
          </div>
        ) : tips && tips.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-5">
            {tips.map((t, i) => (
              <div
                key={t.title || i}
                data-testid={`training-tip-${i}`}
                className="rounded-3xl border border-border/60 bg-card p-6 warm-shadow flex gap-4"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center font-display font-bold">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display font-bold text-lg text-accent">{t.title}</h3>
                  <p className="mt-1 text-sm text-accent/80 leading-relaxed">{t.tip}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="training-tips-empty">
            Training tips are being prepared for this breed — check back shortly.
          </p>
        )}
      </div>

      {/* Recommended training guides */}
      {recGuides.length > 0 && (
        <div className="mt-16" data-testid="recommended-guides-section">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen size={22} className="text-primary" />
            <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">
              Training guides for a {breed.name}
            </h2>
          </div>
          <p className="text-muted-foreground max-w-2xl mb-6">
            Expert-written guides picked for a {breed.name}&apos;s temperament and needs.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`recommended-guide-${g.slug}`}
                className="group rounded-3xl border border-border/60 warm-shadow p-6 bg-card hover:-translate-y-1 transition-transform flex flex-col justify-between min-h-[200px]"
              >
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
                    Training & Behaviour
                  </span>
                  <h3 className="mt-3 font-display font-bold text-xl text-accent leading-tight">{g.title}</h3>
                  <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{g.excerpt}</p>
                </div>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground inline-flex items-center gap-1">
                    <Clock size={14} /> {g.read_min} min read
                  </span>
                  <span className="text-primary font-semibold inline-flex items-center gap-1">
                    Read <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Health & Nutrition */}
      <div className="mt-16" data-testid="health-nutrition-section">
        <div className="flex items-center gap-3 mb-2">
          <HeartPulse size={22} className="text-primary" />
          <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">
            Health &amp; nutrition for a {breed.name}
          </h2>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          What to watch for, how to feed, and how to keep a {breed.name} in top shape — tuned to this breed&apos;s size and build.
        </p>

        {healthLoading ? (
          <div className="grid md:grid-cols-3 gap-5" data-testid="health-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-3xl border border-border/60 bg-card p-6 animate-pulse">
                <div className="h-5 w-1/2 bg-muted rounded mb-4" />
                <div className="h-3 w-full bg-muted rounded mb-2" />
                <div className="h-3 w-5/6 bg-muted rounded mb-2" />
                <div className="h-3 w-4/6 bg-muted rounded" />
              </div>
            ))}
          </div>
        ) : health && health.length > 0 ? (
          <>
            <div className="grid md:grid-cols-3 gap-5">
              {health.map((s) => {
                const meta = {
                  health_concerns: { title: "Common health concerns", icon: HeartPulse },
                  feeding: { title: "Feeding & nutrition", icon: Bone },
                  exercise: { title: "Exercise & weight", icon: Activity },
                }[s.key] || { title: s.key, icon: HeartPulse };
                const Icon = meta.icon;
                return (
                  <div
                    key={s.key}
                    data-testid={`health-card-${s.key}`}
                    className="rounded-3xl border border-border/60 bg-card p-6 warm-shadow"
                  >
                    <div className="flex items-center gap-2.5 mb-4">
                      <span className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center">
                        <Icon size={17} />
                      </span>
                      <h3 className="font-display font-bold text-lg text-accent leading-tight">{meta.title}</h3>
                    </div>
                    <ul className="space-y-2.5">
                      {s.points.map((p, i) => (
                        <li key={i} className="flex gap-2 text-sm text-accent/80 leading-relaxed">
                          <span className="text-primary mt-0.5 shrink-0">•</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Educational guidance only — not veterinary advice. Always consult a licensed vet for your dog&apos;s specific health needs.
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="health-empty">
            Health &amp; nutrition guidance is being prepared for this breed — check back shortly.
          </p>
        )}
      </div>

      {/* Adoption CTA */}
      <div className="mt-16">
        <div className="rounded-[2rem] bg-secondary p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">
              Ready to meet a {breed.name}?
            </h2>
            <p className="text-secondary-foreground/80 mt-2">
              Search animal shelters and rescues across Australia — and consider bringing a certified trainer to your first meet-and-greet.
            </p>
          </div>
          <Link
            to="/adoption"
            data-testid="breed-adopt-cta"
            className="pill-btn shrink-0 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold"
          >
            Find shelters near me <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
